import { useCallback, useEffect, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import type { BillingPeriodsView, SupportedLocale } from '@stm/contracts';
import { translate } from '@stm/i18n';
import { apiRequest } from './api';
import { formatCalendarDate } from './athletes';
import { previousDay } from './billing';
import { errorKeyOf, type ErrorKey } from './BillingForms';

export interface BillingPeriodsPanelProps {
  locale: SupportedLocale;
  organizationId: string;
  csrfToken: string;
}

/**
 * Finance's period lock: closes the ledger through a past day. A close is final, so the form asks
 * for an explicit confirmation, and one idempotency key lives as long as the form content.
 */
export function BillingPeriodsPanel({
  locale,
  organizationId,
  csrfToken,
}: BillingPeriodsPanelProps): React.JSX.Element {
  const base = `/api/v1/organizations/${organizationId}/billing/periods`;
  const [periods, setPeriods] = useState<BillingPeriodsView | null>(null);
  const [errorKey, setErrorKey] = useState<ErrorKey | null>(null);
  const [closedThrough, setClosedThrough] = useState('');
  const [note, setNote] = useState('');
  const [confirmed, setConfirmed] = useState(false);
  const [done, setDone] = useState(false);
  const postKey = useRef<{ signature: string; key: string } | null>(null);

  const load = useCallback(async () => {
    try {
      setPeriods(await apiRequest<BillingPeriodsView>(base));
    } catch (error) {
      setErrorKey(errorKeyOf(error));
    }
  }, [base]);

  useEffect(() => {
    void load();
  }, [load]);

  const formValid = closedThrough !== '' && note.trim() !== '' && confirmed;

  async function closePeriod(): Promise<void> {
    if (!formValid) return;
    setErrorKey(null);
    setDone(false);
    const signature = JSON.stringify({ closedThrough, note: note.trim() });
    if (postKey.current?.signature !== signature) {
      postKey.current = { signature, key: crypto.randomUUID() };
    }
    try {
      setPeriods(
        await apiRequest<BillingPeriodsView>(base, {
          method: 'POST',
          csrfToken,
          idempotencyKey: postKey.current.key,
          body: { closedThrough, note: note.trim() },
        }),
      );
      postKey.current = null;
      setClosedThrough('');
      setNote('');
      setConfirmed(false);
      setDone(true);
    } catch (error) {
      setErrorKey(errorKeyOf(error));
    }
  }

  return (
    <Box component="section" aria-labelledby="periods-title" sx={{ mt: 4 }}>
      <Typography variant="h5" component="h2" id="periods-title" gutterBottom>
        {translate(locale, 'billing.periods.title')}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {translate(locale, 'billing.periods.note')}
      </Typography>
      {errorKey !== null && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {translate(locale, errorKey)}
        </Alert>
      )}
      {done && (
        <Alert severity="success" sx={{ mb: 2 }}>
          {translate(locale, 'billing.periods.done')}
        </Alert>
      )}
      {periods !== null && (
        <Typography sx={{ mb: 2 }}>
          {periods.closedThrough === null
            ? translate(locale, 'billing.periods.none')
            : `${translate(locale, 'billing.periods.closedThrough')}: ${formatCalendarDate(periods.closedThrough, locale)}`}
        </Typography>
      )}
      <Stack
        component="form"
        spacing={2}
        sx={{ maxWidth: 480, mb: 3 }}
        onSubmit={(event) => {
          event.preventDefault();
          void closePeriod();
        }}
      >
        <TextField
          required
          size="small"
          type="date"
          label={translate(locale, 'billing.periods.field.closedThrough')}
          value={closedThrough}
          onChange={(event) => {
            setClosedThrough(event.target.value);
          }}
          slotProps={{
            inputLabel: { shrink: true },
            htmlInput: { max: periods === null ? undefined : previousDay(periods.today) },
          }}
        />
        <TextField
          required
          size="small"
          label={translate(locale, 'billing.periods.field.note')}
          value={note}
          onChange={(event) => {
            setNote(event.target.value);
          }}
          slotProps={{ htmlInput: { maxLength: 500 } }}
        />
        <FormControlLabel
          control={
            <Checkbox
              checked={confirmed}
              onChange={(event) => {
                setConfirmed(event.target.checked);
              }}
            />
          }
          label={translate(locale, 'billing.periods.field.confirm')}
        />
        <Box>
          <Button type="submit" variant="contained" disabled={!formValid}>
            {translate(locale, 'billing.periods.submit')}
          </Button>
        </Box>
      </Stack>
      {periods !== null && periods.closes.length > 0 && (
        <Box component="section" aria-labelledby="periods-history-title">
          <Typography variant="subtitle1" component="h3" id="periods-history-title" gutterBottom>
            {translate(locale, 'billing.periods.history')}
          </Typography>
          <Stack component="ul" spacing={1} sx={{ listStyle: 'none', padding: 0, margin: 0 }}>
            {periods.closes.map((close) => (
              <Box component="li" key={close.id}>
                <Typography>
                  {translate(locale, 'billing.periods.closedThrough')}:{' '}
                  {formatCalendarDate(close.closedThrough, locale)}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {translate(locale, 'billing.periods.closedAt')}:{' '}
                  {formatCalendarDate(close.closedAt.slice(0, 10), locale)} · {close.note}
                </Typography>
              </Box>
            ))}
          </Stack>
        </Box>
      )}
    </Box>
  );
}
